import React from "react";
import { Bookmark, BookmarkCheck, TrendingUp, Users, DollarSign, Target, ChevronDown, ChevronUp, Rocket } from "lucide-react";
import { APP } from "@/constants/testIds";

export default function IdeaCard({ idea, isSaved, onSave, expanded, onToggle, onDeepDive }) {
  return (
    <div data-testid={APP.ideaCard} className="brutal-border p-6 flex flex-col bg-[color:var(--bg)] relative">
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className="tag">{idea.category}</span>
        <div className="flex items-center gap-2 text-xs font-mono-tight">
          <TrendingUp size={12} />
          <span className="font-bold">{idea.trend_score}</span>
        </div>
      </div>
      <h3 data-testid={APP.ideaTitle} className="font-display text-2xl leading-tight tracking-tight">
        {idea.title}
      </h3>
      <p className="text-sm text-[color:var(--muted)] mt-2 leading-relaxed italic">{idea.tagline}</p>

      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <MetaCell icon={DollarSign} label="Cost" value={idea.startup_cost} />
        <MetaCell icon={Target} label="Market" value={idea.market_size} />
        <MetaCell icon={Users} label="Skill" value={idea.skill_level} />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Model</div>
          <div className="font-semibold mt-0.5 truncate" title={idea.revenue_model}>
            {idea.revenue_model.split(" ").slice(0, 4).join(" ")}…
          </div>
        </div>
      </div>

      {expanded && (
        <div className="mt-5 pt-5 border-t border-[color:var(--line)] space-y-4 text-sm">
          <Section label="Description">{idea.description}</Section>
          <Section label="Target market">{idea.target_market}</Section>
          <Section label="Revenue model">{idea.revenue_model}</Section>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">First steps</div>
            <ol className="mt-2 space-y-1.5 list-none">
              {idea.first_steps.map((step, i) => (
                <li key={`${idea.id}-step-${step.slice(0, 20)}-${i}`} className="flex gap-3">
                  <span className="font-mono-tight text-[color:var(--muted)] text-xs mt-1">0{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Competitors</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {idea.competitor_examples.map((c) => (
                <span key={`${idea.id}-comp-${c}`} className="tag border-[color:var(--fg)]">{c}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-[color:var(--line)] flex items-center gap-2">
        <button
          data-testid={APP.ideaExpandBtn}
          onClick={onToggle}
          className="py-2.5 px-3 border border-[color:var(--fg)] text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)] transition-colors flex items-center justify-center gap-1"
        >
          {expanded ? <>Less <ChevronUp size={14} /></> : <>Details <ChevronDown size={14} /></>}
        </button>
        <button
          data-testid={APP.ideaDeepDiveBtn}
          onClick={() => onDeepDive(idea)}
          className="flex-1 py-2.5 px-3 border border-[color:var(--fg)] text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--hot)] hover:border-[color:var(--hot)] hover:text-white transition-colors flex items-center justify-center gap-2"
        >
          <Rocket size={13} /> Deep Dive
        </button>
        <button
          data-testid={APP.ideaSaveBtn}
          onClick={() => onSave(idea)}
          disabled={isSaved}
          className={`py-2.5 px-4 text-xs uppercase tracking-widest font-semibold flex items-center gap-2 transition-colors ${
            isSaved
              ? "bg-[color:var(--yellow)] text-[color:var(--fg)] border border-[color:var(--fg)]"
              : "bg-[color:var(--fg)] text-[color:var(--bg)] hover:opacity-90"
          }`}
        >
          {isSaved ? <><BookmarkCheck size={14} /> Saved</> : <><Bookmark size={14} /> Save</>}
        </button>
      </div>
    </div>
  );
}

function MetaCell({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)] flex items-center gap-1">
        <Icon size={10} /> {label}
      </div>
      <div className="font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">{label}</div>
      <p className="mt-1 leading-relaxed">{children}</p>
    </div>
  );
}
